import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, Plus, Trash2, Users, DollarSign, Tag, Clock, Eye, EyeOff,
  Check, ZoomIn, ZoomOut, RotateCcw, Layers, ChevronDown, ChevronUp,
  AlertCircle, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const TICKET_TYPES = [
  { value: 'standard', label: 'Entrada Estándar' },
  { value: 'vip', label: 'VIP' },
  { value: 'early_bird', label: 'Early Bird' },
  { value: 'group', label: 'Grupo' },
  { value: 'table', label: 'Mesa/Reservado' },
];

// Shape path generator for venue elements (matching VenueEditor)
const getShapePath = (shape, width, height) => {
  switch (shape) {
    case 'rounded': 
      const r = Math.min(8, width/4, height/4); 
      return `M ${r} 0 L ${width-r} 0 Q ${width} 0 ${width} ${r} L ${width} ${height-r} Q ${width} ${height} ${width-r} ${height} L ${r} ${height} Q 0 ${height} 0 ${height-r} L 0 ${r} Q 0 0 ${r} 0 Z`;
    case 'circle': 
      const rx = width / 2; 
      const ry = height / 2;
      return `M ${rx} 0 A ${rx} ${ry} 0 1 1 ${rx} ${height} A ${rx} ${ry} 0 1 1 ${rx} 0 Z`;
    case 'hexagon': 
      return `M ${width*0.25} 0 L ${width*0.75} 0 L ${width} ${height*0.5} L ${width*0.75} ${height} L ${width*0.25} ${height} L 0 ${height*0.5} Z`;
    case 'triangle': 
      return `M ${width/2} 0 L ${width} ${height} L 0 ${height} Z`;
    case 'diamond': 
      return `M ${width/2} 0 L ${width} ${height/2} L ${width/2} ${height} L 0 ${height/2} Z`;
    case 'star': 
      let p = ''; 
      for (let i = 0; i < 10; i++) { 
        const r2 = i % 2 === 0 ? Math.min(width,height)/2 : (Math.min(width,height)/2)*0.4; 
        const a = (i*Math.PI/5)-Math.PI/2; 
        p += (i===0?'M ':'L ')+(width/2+r2*Math.cos(a))+' '+(height/2+r2*Math.sin(a))+' '; 
      } 
      return p+'Z';
    case 'trapezoid': 
      return `M ${width*0.15} 0 L ${width*0.85} 0 L ${width} ${height} L 0 ${height} Z`;
    case 'arc': 
      return `M 0 ${height} Q ${width*0.5} ${-height*0.3} ${width} ${height} L ${width*0.85} ${height} Q ${width*0.5} ${height*0.2} ${width*0.15} ${height} Z`;
    case 'lshape':
      return `M 0 0 L ${width*0.4} 0 L ${width*0.4} ${height*0.5} L ${width} ${height*0.5} L ${width} ${height} L 0 ${height} Z`;
    default: 
      return `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`;
  }
};

// STYLES config to match VenueEditor
const STYLES = [
  { id: 'classic', label: 'Clásico', gradient: false, pattern: 'none', opacity: 0.7 },
  { id: 'modern', label: 'Moderno', gradient: true, pattern: 'dots', opacity: 0.75, gradientType: 'diagonal' },
  { id: 'minimalist', label: 'Minimalista', gradient: false, pattern: 'none', opacity: 0.5, stroke: 'dashed' },
  { id: 'futuristic', label: 'Futurista', gradient: true, pattern: 'grid', opacity: 0.6, glow: true },
  { id: 'neon', label: 'Neón', gradient: false, pattern: 'none', opacity: 0.4, glow: true, neonBorder: true },
  { id: 'glass', label: 'Cristal', gradient: false, pattern: 'none', opacity: 0.3, blur: true },
  { id: 'brutalist', label: 'Brutalista', gradient: false, pattern: 'noise', opacity: 0.85, thickBorder: true, roughEdge: true },
  { id: 'holographic', label: 'Holográfico', gradient: true, pattern: 'holo', opacity: 0.6, holographic: true },
  { id: 'luxury', label: 'Luxury', gradient: true, pattern: 'metallic', opacity: 0.9, metallic: true, goldTrim: true },
];

const VenueTicketEditor = ({ 
  venue, 
  tickets, 
  setTickets,
  onAddTicket,
  onRemoveTicket,
  onUpdateTicket
}) => {
  const [zoom, setZoom] = useState(1);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  
  // Types that are NOT sellable (structural/decorative/guide elements)
  const NON_SELLABLE_TYPES = [
    'wall', 'corner', 'pillar', 'bathroom', 'exit', 'stairs',  // Structural
    'bar', 'djbooth', 'stage',  // Guide/decorative (not audience areas)
    'decoration', 'sign', 'guide', 'info'  // Other non-sellable
  ];
  
  // Get ALL sections/elements from venue (for rendering on map)
  const allElements = useMemo(() => {
    const items = venue?.sections || venue?.elements || [];
    return items.filter(el => el.name);
  }, [venue]);
  
  // Get ONLY sellable sections (for ticket creation)
  const sellableSections = useMemo(() => {
    return allElements.filter(el => 
      !NON_SELLABLE_TYPES.includes(el.type) && 
      (el.capacity > 0 || el.type === 'section' || el.type === 'lounge' || el.type === 'table')
    );
  }, [allElements]);
  
  // For backwards compatibility, use sellableSections for ticket operations
  const sections = sellableSections;
  
  // Map tickets to sections
  const ticketBySectionId = useMemo(() => {
    const map = {};
    tickets.forEach(t => {
      if (t.linked_section_id) {
        map[t.linked_section_id] = t;
      }
    });
    return map;
  }, [tickets]);
  
  // Get unlinked tickets
  const unlinkedTickets = useMemo(() => 
    tickets.filter(t => !t.linked_section_id),
    [tickets]
  );
  
  // Canvas dimensions
  const canvasWidth = venue?.canvas_width || 800;
  const canvasHeight = venue?.canvas_height || 500;
  
  // Calculate scale for map display
  const mapWidth = 400;
  const baseScale = mapWidth / canvasWidth;
  const scale = baseScale * zoom;
  const mapHeight = canvasHeight * baseScale * zoom;
  
  // Handle section click - create or select ticket for this section
  const handleSectionClick = (section) => {
    setSelectedSectionId(section.id);
    
    // Check if this section already has a ticket
    const existingTicket = ticketBySectionId[section.id];
    
    if (existingTicket) {
      // Expand the existing ticket
      setExpandedTicketId(existingTicket.id);
    } else {
      // Create a new ticket for this section
      const newTicket = {
        id: Date.now(),
        name: section.name,
        type: section.type === 'lounge' ? 'vip' : section.type === 'table' ? 'table' : 'standard',
        stock: section.capacity || 100,
        price: 0,
        max_per_order: section.type === 'table' ? 1 : 4,
        description: '',
        sale_end_date: '',
        entry_limit_time: '',
        is_visible: true,
        is_active: true,
        discount_code: '',
        discount_percent: 0,
        linked_section_id: section.id,
        linked_section_name: section.name,
        linked_section_color: section.color,
        // Tier type & multi-stage support
        tier_type: 'sale',
        inquiry_link: '',
        inquiry_label: '',
        price_stages: []
      };
      
      setTickets(prev => [...prev, newTicket]);
      setExpandedTicketId(newTicket.id);
    }
  };
  
  // Auto-fill: Create tickets for ALL sections at once
  const handleAutoFillAll = () => {
    const newTickets = [];
    let timestamp = Date.now();
    
    sections.forEach(section => {
      // Skip if section already has a ticket
      if (ticketBySectionId[section.id]) return;
      
      const pos = section.position || {};
      newTickets.push({
        id: timestamp++,
        name: section.name,
        type: section.type === 'lounge' ? 'vip' : section.type === 'table' ? 'table' : 'standard',
        stock: section.capacity || 100,
        price: 0,
        max_per_order: section.type === 'table' ? 1 : 4,
        description: '',
        sale_end_date: '',
        entry_limit_time: '',
        is_visible: true,
        is_active: true,
        discount_code: '',
        discount_percent: 0,
        linked_section_id: section.id,
        linked_section_name: section.name,
        linked_section_color: section.color,
        tier_type: 'sale',
        inquiry_link: '',
        inquiry_label: '',
        price_stages: []
      });
    });
    
    if (newTickets.length > 0) {
      setTickets(prev => [...prev, ...newTickets]);
    }
  };
  
  // Count sections without tickets
  const sectionsWithoutTickets = sections.filter(s => !ticketBySectionId[s.id]).length;
  
  // Check if section has a ticket configured
  // Check if element is sellable
  const isSellable = (element) => {
    return !NON_SELLABLE_TYPES.includes(element.type) && 
      (element.capacity > 0 || element.type === 'section' || element.type === 'lounge' || element.type === 'table');
  };
  
  const getSectionStatus = (section) => {
    if (!isSellable(section)) return 'not_sellable';
    const ticket = ticketBySectionId[section.id];
    if (!ticket) return 'empty';
    if (ticket.price > 0 && ticket.stock > 0) return 'configured';
    return 'partial';
  };
  
  // Render venue section on map with proper shapes
  const renderSection = (section) => {
    const pos = section.position || { x: section.x || 0, y: section.y || 0, width: section.width || 100, height: section.height || 100 };
    const x = pos.x * scale;
    const y = pos.y * scale;
    const width = Math.max(pos.width * scale, 10);
    const height = Math.max(pos.height * scale, 10);
    const rotation = section.rotation || 0;
    const shape = section.shape || 'rect';
    const style = section.style || 'modern';
    const styleConfig = STYLES.find(s => s.id === style) || STYLES[1];
    
    const status = getSectionStatus(section);
    const isSelected = selectedSectionId === section.id;
    const ticket = ticketBySectionId[section.id];
    const sellable = isSellable(section);
    
    // Generate the SVG path for this shape
    const shapePath = getShapePath(shape, width, height);
    
    // Status colors
    const statusColors = {
      not_sellable: 'rgba(0,0,0,0.3)',
      empty: 'rgba(255,255,255,0.1)',
      partial: 'rgba(251,191,36,0.3)',
      configured: 'rgba(34,197,94,0.3)'
    };
    
    // Calculate center for rotation
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    
    // Get fill based on style
    const getFill = () => {
      if (styleConfig.gradient || styleConfig.holographic || styleConfig.metallic) {
        return `url(#grad-preview-${section.id})`;
      }
      const opacity = sellable ? (isSelected ? 0.9 : styleConfig.opacity) : 0.4;
      return `${section.color || '#4F46E5'}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
    };
    
    return (
      <g 
        key={section.id}
        style={{ cursor: sellable ? 'pointer' : 'default' }}
        onClick={() => sellable && handleSectionClick(section)}
        transform={rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined}
      >
        <defs>
          {/* Gradient definitions for styled elements */}
          {styleConfig.gradientType === 'diagonal' && (
            <linearGradient id={`grad-preview-${section.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={section.color || '#4F46E5'} stopOpacity={styleConfig.opacity + 0.15} />
              <stop offset="50%" stopColor={section.color || '#4F46E5'} stopOpacity={styleConfig.opacity} />
              <stop offset="100%" stopColor={section.color || '#4F46E5'} stopOpacity={styleConfig.opacity - 0.2} />
            </linearGradient>
          )}
          {styleConfig.holographic && (
            <linearGradient id={`grad-preview-${section.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ec4899" stopOpacity={0.7} />
              <stop offset="20%" stopColor="#8b5cf6" stopOpacity={0.7} />
              <stop offset="40%" stopColor="#06b6d4" stopOpacity={0.7} />
              <stop offset="60%" stopColor="#22c55e" stopOpacity={0.7} />
              <stop offset="80%" stopColor="#eab308" stopOpacity={0.7} />
              <stop offset="100%" stopColor="#ec4899" stopOpacity={0.7} />
            </linearGradient>
          )}
          {styleConfig.metallic && (
            <linearGradient id={`grad-preview-${section.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.95} />
              <stop offset="25%" stopColor={section.color || '#4F46E5'} stopOpacity={0.85} />
              <stop offset="50%" stopColor="#FFD700" stopOpacity={0.6} />
              <stop offset="75%" stopColor={section.color || '#4F46E5'} stopOpacity={0.85} />
              <stop offset="100%" stopColor="#B8860B" stopOpacity={0.9} />
            </linearGradient>
          )}
          {(styleConfig.glow || styleConfig.neonBorder) && (
            <filter id={`glow-preview-${section.id}`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          )}
        </defs>
        
        {/* Selection glow - only for sellable */}
        {isSelected && sellable && (
          <path
            d={shapePath}
            transform={`translate(${x - 3}, ${y - 3}) scale(${(width + 6) / width}, ${(height + 6) / height})`}
            fill="none"
            stroke="#8b5cf6"
            strokeWidth="3"
            opacity="0.6"
          />
        )}
        
        {/* Neon glow effect */}
        {styleConfig.neonBorder && sellable && (
          <path
            d={shapePath}
            transform={`translate(${x}, ${y})`}
            fill="none"
            stroke={section.color || '#4F46E5'}
            strokeWidth="4"
            opacity="0.3"
            filter={`url(#glow-preview-${section.id})`}
          />
        )}
        
        {/* Main section shape */}
        <path
          d={shapePath}
          transform={`translate(${x}, ${y})`}
          fill={getFill()}
          stroke={isSelected ? '#fff' : (styleConfig.goldTrim ? '#D4AF37' : 'rgba(255,255,255,0.3)')}
          strokeWidth={isSelected ? 2 : (styleConfig.thickBorder ? 3 : 1)}
          strokeDasharray={!sellable ? '4 2' : (styleConfig.stroke === 'dashed' ? '6 3' : 'none')}
        />
        
        {/* Status overlay - only for sellable */}
        {sellable && (
          <path
            d={shapePath}
            transform={`translate(${x}, ${y})`}
            fill={statusColors[status]}
          />
        )}
        
        {/* Non-sellable label */}
        {!sellable && width > 50 && height > 30 && (
          <text
            x={x + width/2}
            y={y + height/2 + 12}
            textAnchor="middle"
            fill="rgba(255,255,255,0.4)"
            fontSize={8}
            style={{ pointerEvents: 'none' }}
          >
            (no vendible)
          </text>
        )}
        
        {/* Section name */}
        {width > 40 && height > 25 && (
          <text
            x={x + width/2}
            y={y + height/2 - (ticket ? 6 : (!sellable ? 4 : 0))}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={sellable ? (styleConfig.metallic ? '#D4AF37' : '#fff') : 'rgba(255,255,255,0.6)'}
            fontSize={Math.min(11, width / 8)}
            fontWeight="bold"
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)', pointerEvents: 'none' }}
          >
            {section.name.length > 12 ? section.name.substring(0, 10) + '...' : section.name}
          </text>
        )}
        
        {/* Price badge if configured */}
        {sellable && ticket && ticket.price > 0 && width > 50 && (
          <text
            x={x + width/2}
            y={y + height/2 + 10}
            textAnchor="middle"
            fill="#22c55e"
            fontSize={9}
            fontWeight="bold"
            style={{ pointerEvents: 'none' }}
          >
            ${ticket.price.toLocaleString()}
          </text>
        )}
        
        {/* Configured checkmark */}
        {status === 'configured' && (
          <g transform={`translate(${x + width - 14}, ${y + 4})`}>
            <circle cx="6" cy="6" r="6" fill="#22c55e"/>
            <path d="M 3 6 L 5 8 L 9 4" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </g>
        )}
        
        {/* Empty indicator - only for sellable */}
        {status === 'empty' && sellable && (
          <g transform={`translate(${x + width - 14}, ${y + 4})`}>
            <circle cx="6" cy="6" r="6" fill="rgba(255,255,255,0.2)"/>
            <path d="M 3 6 L 9 6 M 6 3 L 6 9" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
          </g>
        )}
      </g>
    );
  };
  
  // Render ticket card
  const renderTicketCard = (ticket, index) => {
    const isExpanded = expandedTicketId === ticket.id;
    const linkedSection = sections.find(s => s.id === ticket.linked_section_id);
    
    return (
      <motion.div
        key={ticket.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`border rounded-xl overflow-hidden transition-all ${
          isExpanded 
            ? 'border-violet-500/50 bg-violet-500/5' 
            : 'border-white/10 bg-white/[0.02]'
        }`}
      >
        {/* Header - always visible */}
        <div 
          className="p-3 cursor-pointer flex items-center gap-3"
          onClick={() => {
            setExpandedTicketId(isExpanded ? null : ticket.id);
            if (ticket.linked_section_id) {
              setSelectedSectionId(ticket.linked_section_id);
            }
          }}
        >
          {/* Color indicator */}
          <div 
            className="w-3 h-10 rounded-full flex-shrink-0"
            style={{ backgroundColor: ticket.linked_section_color || linkedSection?.color || '#4F46E5' }}
          />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={ticket.name}
                onChange={(e) => onUpdateTicket(ticket.id, 'name', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Nombre de la entrada"
                className="bg-transparent border-none text-white font-medium text-sm focus:outline-none focus:ring-0 w-full"
              />
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
              {linkedSection && (
                <span className="flex items-center gap-1">
                  <Layers size={10} />
                  {linkedSection.name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users size={10} />
                {ticket.stock}
              </span>
              <span className="flex items-center gap-1 text-emerald-500">
                <DollarSign size={10} />
                {ticket.price > 0 ? `$${ticket.price.toLocaleString()}` : 'Gratis'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {ticket.price > 0 && ticket.stock > 0 && (
              <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check size={12} className="text-emerald-500" />
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveTicket(ticket.id);
              }}
              className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400"
            >
              <Trash2 size={14} />
            </Button>
            {isExpanded ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
          </div>
        </div>
        
        {/* Expanded content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 pt-2 border-t border-white/5 space-y-4">
                {/* Tipo de tanda — Venta online / Consultar */}
                <div>
                  <Label className="text-zinc-500 text-xs mb-1.5 block">Tipo de tanda</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid={`venue-tier-type-${ticket.id}`}>
                    {[
                      { id: 'sale', label: 'Venta online', desc: 'Compra directa', icon: '🛒' },
                      { id: 'inquiry', label: 'Consultar', desc: 'Chat interno con cliente', icon: '💬' },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdateTicket(ticket.id, 'tier_type', opt.id);
                        }}
                        className={`p-2.5 rounded-lg border text-left transition-all ${(ticket.tier_type || 'sale') === opt.id ? 'border-violet-500 bg-violet-500/10' : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/40'}`}
                        data-testid={`venue-tier-type-${opt.id}-${ticket.id}`}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-base">{opt.icon}</span>
                          <span className="text-xs text-white font-semibold">{opt.label}</span>
                        </div>
                        <p className="text-[10px] text-zinc-500">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Inquiry-only fields */}
                {ticket.tier_type === 'inquiry' && (
                  <div className="space-y-2.5 p-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5">
                    <p className="text-[11px] text-cyan-200 leading-relaxed">
                      Esta zona no se vende online. Cuando un cliente la elija, se abre un chat <strong>dentro de la plataforma</strong> entre vos y el cliente. Las consultas aparecen en el tab <strong>Consultas</strong> del evento.
                    </p>
                    <div>
                      <Label className="text-zinc-400 text-xs">Texto del botón (opcional)</Label>
                      <Input
                        value={ticket.inquiry_label || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => onUpdateTicket(ticket.id, 'inquiry_label', e.target.value)}
                        placeholder="Ej: Consultar disponibilidad"
                        className="bg-zinc-800 border-zinc-700 h-8 text-xs mt-1"
                      />
                      <p className="text-[10px] text-zinc-500 mt-1">Si lo dejás vacío, decimos "Iniciar consulta".</p>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer p-2 rounded-md hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={!!ticket.is_sold_out}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => onUpdateTicket(ticket.id, 'is_sold_out', e.target.checked)}
                        className="mt-0.5 accent-emerald-500"
                      />
                      <div>
                        <p className="text-xs text-white font-medium">Marcar como agotada</p>
                        <p className="text-[10px] text-zinc-400 leading-snug">Si ya vendiste estas mesas, activá esto para que aparezca como "Agotado" en el listado público.</p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Sale-only: price + stock + tandas */}
                {(ticket.tier_type || 'sale') === 'sale' && (
                <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-500 text-xs">Precio único</Label>
                    <div className="relative mt-1">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                      <Input
                        type="number"
                        min="0"
                        value={ticket.price}
                        onChange={(e) => onUpdateTicket(ticket.id, 'price', parseFloat(e.target.value) || 0)}
                        className="pl-8 bg-zinc-800 border-zinc-700 h-9"
                        disabled={(ticket.price_stages || []).length > 0}
                      />
                    </div>
                    {(ticket.price_stages || []).length > 0 && (
                      <p className="text-[10px] text-violet-400 mt-1">Usando precios por tandas ↓</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Stock total</Label>
                    <div className="relative mt-1">
                      <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                      <Input
                        type="number"
                        min="1"
                        value={(ticket.price_stages || []).length > 0
                          ? (ticket.price_stages.reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0) || ticket.stock)
                          : ticket.stock}
                        onChange={(e) => onUpdateTicket(ticket.id, 'stock', parseInt(e.target.value) || 1)}
                        className="pl-8 bg-zinc-800 border-zinc-700 h-9"
                        disabled={(ticket.price_stages || []).length > 0}
                      />
                    </div>
                    {(ticket.price_stages || []).length > 0 && (
                      <p className="text-[10px] text-violet-400 mt-1">Suma automática de tandas</p>
                    )}
                  </div>
                </div>
                </>)}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-500 text-xs">Tipo</Label>
                    <select
                      value={ticket.type}
                      onChange={(e) => onUpdateTicket(ticket.id, 'type', e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white text-sm mt-1"
                    >
                      {TICKET_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Máx. por orden</Label>
                    <Input
                      type="number"
                      min="1"
                      value={ticket.max_per_order}
                      onChange={(e) => onUpdateTicket(ticket.id, 'max_per_order', parseInt(e.target.value) || 1)}
                      className="bg-zinc-800 border-zinc-700 h-9 mt-1"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-4 pt-2">
                  <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ticket.is_visible}
                      onChange={(e) => onUpdateTicket(ticket.id, 'is_visible', e.target.checked)}
                      className="rounded bg-zinc-800 border-zinc-600"
                    />
                    <Eye size={12} />
                    Visible
                  </label>
                  <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ticket.is_active}
                      onChange={(e) => onUpdateTicket(ticket.id, 'is_active', e.target.checked)}
                      className="rounded bg-zinc-800 border-zinc-600"
                    />
                    <Sparkles size={12} />
                    Activa
                  </label>
                </div>

                {/* Tandas - rediseñado: cards numeradas con flecha + auto-quick-add */}
                {(ticket.tier_type || 'sale') === 'sale' && (
                <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-3 space-y-3">
                  <div>
                    <div className="text-xs font-bold text-violet-200 flex items-center gap-1.5">
                      🎯 Tandas de precios
                      <span className="text-[9px] uppercase tracking-wider text-violet-400 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5">Opcional</span>
                    </div>
                    <p className="text-[11px] text-zinc-300 mt-1 leading-snug">
                      Definí <strong>precios distintos según se van vendiendo</strong>. Cuando se acaba una tanda, automáticamente empieza la siguiente.
                    </p>
                  </div>

                  {/* Quick-add: "Cuántas tandas querés?" presets */}
                  {(ticket.price_stages || []).length === 0 && (
                    <div className="space-y-2">
                      <p className="text-[11px] text-zinc-400">¿Cuántas tandas querés crear?</p>
                      <div className="grid grid-cols-4 gap-2">
                        {[2, 3, 4, 5].map(n => (
                          <button
                            key={n}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const basePrice = ticket.price || 0;
                              const baseStock = Math.max(ticket.stock || 10, n);
                              const perStage = Math.floor(baseStock / n);
                              const stages = Array.from({ length: n }, (_, i) => ({
                                label: `Tanda ${i + 1}`,
                                price: basePrice + (basePrice * 0.25 * i),  // +25% por tanda
                                quantity: i === n - 1 ? baseStock - perStage * (n - 1) : perStage,
                              }));
                              onUpdateTicket(ticket.id, 'price_stages', stages);
                            }}
                            className="text-xs py-2 rounded-lg border border-violet-500/40 bg-violet-500/10 hover:bg-violet-500/20 text-white font-semibold transition-colors"
                            data-testid={`venue-tandas-preset-${n}`}
                          >
                            {n} tandas
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-zinc-500 text-center pt-1">Se prellenan con precio +25% por etapa. Lo podés ajustar después.</p>
                    </div>
                  )}

                  {/* Stages rendered as vertical flow with arrows */}
                  {(ticket.price_stages || []).length > 0 && (
                    <div className="space-y-2">
                      {(ticket.price_stages || []).map((stage, idx) => (
                        <div key={idx}>
                          <div className="rounded-lg border border-violet-500/40 bg-zinc-900/70 p-3 space-y-2 relative">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                {idx + 1}
                              </div>
                              <Input
                                value={stage.label || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const stages = [...ticket.price_stages];
                                  stages[idx] = { ...stages[idx], label: e.target.value };
                                  onUpdateTicket(ticket.id, 'price_stages', stages);
                                }}
                                placeholder="Nombre (Preventa, 2da Tanda, Última…)"
                                className="bg-zinc-800 border-zinc-700 h-8 text-xs flex-1"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const stages = (ticket.price_stages || []).filter((_, i) => i !== idx);
                                  onUpdateTicket(ticket.id, 'price_stages', stages);
                                }}
                                className="text-red-400 hover:text-red-300 text-sm px-2"
                                title="Eliminar tanda"
                              >✕</button>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pl-9">
                              <div>
                                <Label className="text-zinc-500 text-[10px]">Precio</Label>
                                <div className="relative mt-0.5">
                                  <DollarSign size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                                  <Input
                                    type="number"
                                    min="0"
                                    value={stage.price || 0}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const stages = [...ticket.price_stages];
                                      stages[idx] = { ...stages[idx], price: parseFloat(e.target.value) || 0 };
                                      onUpdateTicket(ticket.id, 'price_stages', stages);
                                    }}
                                    className="bg-zinc-800 border-zinc-700 h-7 pl-6 text-xs"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label className="text-zinc-500 text-[10px]">Cantidad</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={stage.quantity || 0}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    const stages = [...ticket.price_stages];
                                    stages[idx] = { ...stages[idx], quantity: parseInt(e.target.value) || 0 };
                                    onUpdateTicket(ticket.id, 'price_stages', stages);
                                  }}
                                  className="bg-zinc-800 border-zinc-700 h-7 text-xs mt-0.5"
                                />
                              </div>
                            </div>
                          </div>
                          {/* Arrow indicator between stages */}
                          {idx < (ticket.price_stages || []).length - 1 && (
                            <div className="flex items-center justify-center my-1">
                              <div className="flex items-center gap-1.5 text-[10px] text-violet-400 italic">
                                <span className="text-violet-500">↓</span>
                                cuando se agote esta, empieza la siguiente
                                <span className="text-violet-500">↓</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Add another + Reset */}
                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const stages = ticket.price_stages || [];
                            const last = stages[stages.length - 1] || { price: ticket.price || 0, quantity: 10 };
                            onUpdateTicket(ticket.id, 'price_stages', [
                              ...stages,
                              { label: `Tanda ${stages.length + 1}`, price: (last.price || 0) * 1.25, quantity: last.quantity || 10 }
                            ]);
                          }}
                          className="flex-1 text-[11px] py-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 hover:bg-violet-500/20 text-violet-200 font-semibold"
                        >+ Agregar tanda</button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateTicket(ticket.id, 'price_stages', []);
                          }}
                          className="text-[11px] py-1.5 px-3 rounded-md bg-white/5 hover:bg-white/10 text-white/60"
                        >Quitar tandas</button>
                      </div>

                      {/* Summary */}
                      <div className="rounded-md bg-violet-500/10 border border-violet-500/30 px-3 py-2 mt-1">
                        <p className="text-[11px] text-violet-200 font-semibold">
                          Stock total: {(ticket.price_stages || []).reduce((s, st) => s + (parseInt(st.quantity) || 0), 0)} entradas
                        </p>
                        <p className="text-[10px] text-zinc-400 mt-0.5">
                          Empieza vendiendo a ${(ticket.price_stages?.[0]?.price || 0).toLocaleString()} y termina a ${(ticket.price_stages?.[ticket.price_stages.length - 1]?.price || 0).toLocaleString()}.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };
  
  if (!venue) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <AlertCircle size={32} className="mx-auto mb-3 opacity-50" />
        <p>No hay venue seleccionado</p>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: Venue Map */}
      <div className="bg-zinc-900/50 rounded-xl border border-white/10 overflow-hidden">
        <div className="p-3 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <Layers size={14} className="text-violet-500" />
            {venue.name}
          </h3>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} className="h-7 w-7 p-0">
              <ZoomOut size={14} />
            </Button>
            <span className="text-xs text-zinc-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(2, z + 0.25))} className="h-7 w-7 p-0">
              <ZoomIn size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setZoom(1)} className="h-7 w-7 p-0">
              <RotateCcw size={14} />
            </Button>
          </div>
        </div>
        
        {/* Map */}
        <div className="p-4 overflow-auto" style={{ maxHeight: '450px' }}>
          <svg 
            width={mapWidth * zoom} 
            height={mapHeight}
            className="mx-auto bg-zinc-950 rounded-lg"
            style={{ border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {/* Render all elements (sellable and non-sellable) */}
            {allElements.map(section => renderSection(section))}
          </svg>
        </div>
        
        {/* Legend */}
        <div className="p-3 border-t border-white/10 flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-white/10 border border-white/20"/>
            <span className="text-zinc-500">Sin configurar</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-amber-500/30 border border-amber-500/50"/>
            <span className="text-zinc-500">Parcial</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-500/50"/>
            <span className="text-zinc-500">Configurado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border border-dashed border-zinc-500 opacity-50"/>
            <span className="text-zinc-500">No vendible</span>
          </div>
        </div>
        
        <div className="p-3 border-t border-white/10 bg-violet-500/5">
          <p className="text-xs text-violet-300 text-center">
            Haz clic en una zona vendible para crear o editar su entrada
          </p>
        </div>
      </div>
      
      {/* Right: Tickets List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <Ticket size={14} className="text-emerald-500" />
            Entradas ({tickets.length})
          </h3>
          <div className="flex items-center gap-2">
            {sectionsWithoutTickets > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={handleAutoFillAll}
                className="text-xs bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700"
              >
                <Sparkles size={12} className="mr-1" />
                Crear todas ({sectionsWithoutTickets})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onAddTicket}
              className="text-xs border-zinc-700"
            >
              <Plus size={12} className="mr-1" />
              Manual
            </Button>
          </div>
        </div>
        
        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-emerald-500/10 rounded-lg p-2">
            <div className="text-lg font-bold text-emerald-500">
              {tickets.filter(t => t.price > 0 && t.stock > 0).length}
            </div>
            <div className="text-[10px] text-zinc-500">Configuradas</div>
          </div>
          <div className="bg-amber-500/10 rounded-lg p-2">
            <div className="text-lg font-bold text-amber-500">
              {sections.length - Object.keys(ticketBySectionId).length}
            </div>
            <div className="text-[10px] text-zinc-500">Sin entrada</div>
          </div>
          <div className="bg-violet-500/10 rounded-lg p-2">
            <div className="text-lg font-bold text-violet-500">
              {tickets.reduce((sum, t) => sum + t.stock, 0)}
            </div>
            <div className="text-[10px] text-zinc-500">Stock total</div>
          </div>
        </div>
        
        {/* Tickets list */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
          {tickets.map((ticket, index) => renderTicketCard(ticket, index))}
        </div>
        
        {tickets.length === 0 && (
          <div className="text-center py-8 text-zinc-500">
            <Ticket size={32} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No hay entradas configuradas</p>
            <p className="text-xs mt-1">Haz clic en una sección del mapa para crear una entrada</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueTicketEditor;
